import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { Search, Menu, X, LogOut, LayoutDashboard, Shield, Clapperboard, Crown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/constants";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/browse", label: "Browse" },
  { to: "/creators", label: "Creators" },
  { to: "/subscriptions", label: "Plans" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/80 border-b border-white/10">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 shrink-0">
          <img src="/logo-mark.png" alt="Tazama Africa logo" className="w-9 h-9 rounded-lg object-cover" />
          <span className="font-display font-extrabold text-xl tracking-tight">
            Tazama <span className="text-[#E50914]">Africa</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-4">
          {NAV.map((n) => {
            const active = location.pathname === n.to;
            return (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  active ? "text-white bg-white/10" : "text-white/60 hover:text-white"}`}>
                {n.label}
              </Link>
            );
          })}
        </nav>

        <form onSubmit={submitSearch} className="hidden sm:flex flex-1 max-w-md ml-auto">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input data-testid="nav-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search films, creators, countries…"
              className="w-full h-10 pl-10 pr-4 rounded-full bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#E50914]/60 transition-colors" />
          </div>
        </form>

        <div className="hidden md:flex items-center gap-2 ml-auto sm:ml-2">
          {user ? (
            <div className="relative">
              <button data-testid="nav-user-menu" onClick={() => setMenu((m) => !m)}
                className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <span className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#F59E0B] flex items-center justify-center text-black font-bold text-sm">
                    {user.name?.[0]?.toUpperCase()}
                  </span>
                )}
                <span className="text-xs font-medium max-w-[100px] truncate">{user.name}</span>
              </button>
              {menu && (
                <div className="absolute right-0 mt-2 w-56 glass rounded-xl p-2 shadow-2xl" onMouseLeave={() => setMenu(false)}>
                  <div className="px-3 py-2 border-b border-white/10 mb-1">
                    <p className="text-sm font-semibold truncate">{user.name}</p>
                    <span className="badge-glass mt-1 !bg-[#D4AF37]/20 !border-[#D4AF37]/30 text-[#F5D97A]">
                      {ROLE_LABELS[user.role] || user.role}
                    </span>
                  </div>
                  <MenuItem to="/dashboard" icon={LayoutDashboard} label="My Dashboard" onClick={() => setMenu(false)} testid="menu-dashboard" />
                  {(user.role === "CREATOR" || user.role === "ADMIN") && (
                    <MenuItem to="/creator/dashboard" icon={Clapperboard} label="Creator Studio" onClick={() => setMenu(false)} testid="menu-creator" />
                  )}
                  {user.role === "ADMIN" && (
                    <MenuItem to="/admin" icon={Shield} label="Admin Panel" onClick={() => setMenu(false)} testid="menu-admin" />
                  )}
                  <MenuItem to="/subscriptions" icon={Crown} label="Upgrade Plan" onClick={() => setMenu(false)} testid="menu-plans" />
                  <button data-testid="nav-logout" onClick={async () => { await logout(); setMenu(false); navigate("/"); }}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/70 hover:bg-white/10 hover:text-white transition-colors">
                    <LogOut className="w-4 h-4" /> Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="px-4 py-2 rounded-full text-sm font-medium text-white/80 hover:text-white transition-colors">
                Sign in
              </Link>
              <Link to="/register" data-testid="nav-register" className="px-4 py-2 rounded-full text-sm font-semibold bg-[#E50914] hover:bg-[#FF2A3B] transition-colors">
                Get Started
              </Link>
            </>
          )}
        </div>

        <button className="md:hidden ml-auto p-2" onClick={() => setOpen((o) => !o)} data-testid="nav-mobile-toggle">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden glass border-t border-white/10 px-4 py-4 space-y-2">
          <form onSubmit={submitSearch}>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
              className="w-full h-10 px-4 rounded-full bg-white/5 border border-white/10 text-sm" data-testid="nav-mobile-search" />
          </form>
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="block px-3 py-2 rounded-lg text-white/80 hover:bg-white/10">
              {n.label}
            </Link>
          ))}
          {user ? (
            <>
              <Link to="/dashboard" onClick={() => setOpen(false)} className="block px-3 py-2 rounded-lg text-white/80 hover:bg-white/10">Dashboard</Link>
              {(user.role === "CREATOR" || user.role === "ADMIN") && <Link to="/creator/dashboard" onClick={() => setOpen(false)} className="block px-3 py-2 rounded-lg text-white/80 hover:bg-white/10">Creator Studio</Link>}
              {user.role === "ADMIN" && <Link to="/admin" onClick={() => setOpen(false)} className="block px-3 py-2 rounded-lg text-white/80 hover:bg-white/10">Admin</Link>}
              <button onClick={async () => { await logout(); setOpen(false); navigate("/"); }} className="block w-full text-left px-3 py-2 rounded-lg text-white/80 hover:bg-white/10">Sign out</button>
            </>
          ) : (
            <div className="flex gap-2 pt-2">
              <Link to="/login" onClick={() => setOpen(false)} className="flex-1 text-center px-4 py-2 rounded-full bg-white/10">Sign in</Link>
              <Link to="/register" onClick={() => setOpen(false)} className="flex-1 text-center px-4 py-2 rounded-full bg-[#E50914]">Get Started</Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
}

const MenuItem = ({ to, icon: Icon, label, onClick, testid }) => (
  <Link to={to} onClick={onClick} data-testid={testid}
    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/70 hover:bg-white/10 hover:text-white transition-colors">
    <Icon className="w-4 h-4" /> {label}
  </Link>
);
